import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { spacesAPI } from '../services/api';
import { FaMapMarkerAlt, FaRupeeSign, FaPhone, FaEnvelope, FaArrowLeft } from 'react-icons/fa';

const SpaceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSpaceDetails();
  }, [id]);

  const fetchSpaceDetails = async () => {
    try {
      setLoading(true);
      const response = await spacesAPI.getById(id);
      setSpace(response.data);
    } catch (error) {
      console.error('Error fetching space details:', error);
      setError('Failed to load space details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div style={{ borderTopColor: '#2563eb', borderBottomColor: '#2563eb' }} className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"></div>
      </div>
    );
  }

  if (error || !space) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error || 'Space not found'}</p>
          <Link to="/spaces" style={{ color: '#2563eb' }} className="hover:underline">Back to Spaces</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <button onClick={() => navigate(-1)} style={{ color: '#2563eb' }} className="flex items-center hover:opacity-70 mb-6">
          <FaArrowLeft className="mr-2" />
          Back
        </button>

        {/* Images */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="h-96 bg-gray-300">
            {space.images && space.images[0] ? (
              <img src={space.images[0]} alt={space.title} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">No Image Available</div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-md">
            <h1 className="text-3xl font-bold mb-4">{space.title}</h1>
            <div className="flex items-center text-gray-600 mb-4">
              <FaMapMarkerAlt className="mr-2" />
              <span>{space.location.address}, {space.location.city}</span>
            </div>

            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Description</h2>
              <p className="text-gray-700">{space.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-600">Space Type</p>
                <p className="font-semibold capitalize">{space.spaceType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Area</p>
                <p className="font-semibold">{space.area} sq ft</p>
              </div>
            </div>

            {space.amenities && space.amenities.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {space.amenities.map((amenity, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Card */}
          <div className="bg-white p-6 rounded-lg shadow-md h-fit sticky top-8">
            <div style={{ color: '#003d66' }} className="flex items-center text-3xl font-bold mb-6">
              <FaRupeeSign />
              <span>{space.pricePerMonth.toLocaleString()}</span>
              <span className="text-lg text-gray-600 ml-2">/month</span>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold mb-2">Owner Details</h3>
              <div className="space-y-2 text-gray-700">
                <div className="flex items-center">
                  <FaPhone className="mr-2" />
                  <span>{space.owner.phone || 'Not provided'}</span>
                </div>
                <div className="flex items-center">
                  <FaEnvelope className="mr-2" />
                  <span>{space.owner.email}</span>
                </div>
              </div>
            </div>

            <Link
              to={`/booking/${space._id}`}
              style={{ backgroundColor: '#2563eb' }}
              className="block w-full text-white text-center py-3 rounded-lg transition font-semibold hover:opacity-90"
            >
              Book This Space
            </Link>

            <p className="text-xs text-gray-500 text-center mt-4">
              You won't be charged yet
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceDetailPage;
